// Tick phase 4 (IMPLEMENTATION §2.4, §0.2 R9): Galleries in id order.
//   Firing: the fire cooldown `cd` counts down; when it is 0 and ammo is queued, the oldest ammo fires:
//   payout = floor((V(ammo) × Mp + 500) / 1000) (core/num payoutOf), cd = fireTicks of the mounted gun.
//   Hand-inserted ammo (queue `hand` = 1) pays manual income; everything else is automated income. Fired
//   ammo is booked as `fired` (its units leave the world). progress.firedBest / firedAutoBest record the best
//   ammo tier fired (any / not hand-inserted) for the `fired` unlock triggers (u18, u20).
//   Readout: per one-second bucket, `ready` = ticks a gun was mounted and working (the gun's ready time),
//   `idle` = ticks the gun could fire (cooldown over) but the queue was empty, `full` = ticks the queue was
//   full, `fired`, `supplied` (counted at insert) and `paid`. So idle / ready ≈ 1 − supply / fire rate.
//   No gun: stall NO_GUN (nothing fires). Practice rounds (cold open) are fired by the `tapGallery` command.
import { payoutOf } from '../core/num.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { itemValue } from '../entities.ts';
import { book } from '../ledger.ts';
import { closesBucket, ringSlots } from '../readout.ts';
import { clearStall, raiseStall } from '../stall.ts';
import type { GalleryB, GalleryWin } from '../state.ts';

function bump(xs: Int[], i: Int, by: Int): void {
  xs[i] = (xs[i] ?? 0) + by;
}

/** Cork Popper (T1) stats etc.: the mounted gun's permille multiplier and fire interval. */
export function gunStats(c: Ctx, tier: Int): { multPermille: Int; fireTicks: Int } {
  const g = c.d.guns[tier - 1];
  if (!g) throw new RangeError(`no gun tier ${tier}`);
  return { multPermille: g.multPermille, fireTicks: g.fireTicks };
}

function closeBucket(c: Ctx, w: GalleryWin): void {
  const n = c.d.economy.galleryReadout.buckets;
  w.n = w.n < n ? w.n + 1 : n;
  w.idx = (w.idx + 1) % ringSlots(c.d);
  for (const xs of [w.ready, w.idle, w.full, w.fired, w.supplied, w.paid]) xs[w.idx] = 0;
}

function galleryStep(c: Ctx, g: GalleryB): void {
  const w = g.win;
  if (g.gun === null) {
    raiseStall(c, g, 'NO_GUN');
  } else {
    if (g.stall === 'NO_GUN') clearStall(c, g);
    bump(w.ready, w.idx, 1);
    if (g.cd > 0) g.cd -= 1;
    if (g.cd === 0) {
      const ammo = g.queue.shift();
      if (ammo === undefined) {
        bump(w.idle, w.idx, 1);
      } else {
        const gun = gunStats(c, g.gun.tier);
        const payout = payoutOf(itemValue(c.d, ammo.fam, ammo.tier), gun.multPermille);
        book(c.s, c.d, ammo.fam, ammo.tier, 'fired', 1);
        if (c.s.transient.held === ammo.id) c.s.transient.held = null;
        c.s.wallet.cash += payout;
        c.s.wallet.lifetime += payout;
        c.s.income.accT += payout;
        const p = c.s.progress;
        if (ammo.tier > p.firedBest) p.firedBest = ammo.tier;
        if (ammo.hand === 0) {
          c.s.wallet.lifetimeAuto += payout;
          c.s.income.accA += payout;
          if (c.s.income.autoFrom < 0) c.s.income.autoFrom = c.s.tick; // the HUD's young window (L11)
          if (ammo.tier > p.firedAutoBest) p.firedAutoBest = ammo.tier; // "your own ammo" (u18)
        }
        g.cd = gun.fireTicks;
        bump(w.fired, w.idx, 1);
        bump(w.paid, w.idx, payout);
        c.ev.push({
          e: 'fire',
          bid: g.id,
          gunTier: g.gun.tier,
          ammoTier: ammo.tier,
          payout,
          practice: false,
        });
      }
    }
  }
  if (g.queue.length >= c.d.buildings.gallery.ammoQueue) bump(w.full, w.idx, 1);
}

export function runGalleries(c: Ctx): void {
  const closing = closesBucket(c.d, c.s.tick);
  for (const b of c.s.buildings) {
    if (b.type !== 'gallery') continue;
    if (b.ghost === null && !b.paused) galleryStep(c, b);
    if (closing) closeBucket(c, b.win);
  }
}
